import React, {useEffect,useMemo,useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import {Button,Empty,Space,Tooltip} from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

import {sitesAC} from "../../store/branches/sites/actionCreators";
import {selectSitesState} from "../../store/selectors";
import {AddNewBlock} from "../../components/AddNewBlock";
import SitesBlock from "./components/SitesBlock";
import HeaderSites from "./components/HeaderSites";
import Preloader from "../../components/Preloader";
import {LoadingStatus} from "../../store/status";
import SitesAlert from "../../components/Alerts/sites";
import UserAlert from "../../components/Alerts/user";

import classes from "./Sites.module.scss";
import {userAC} from "../../store/branches/user/actionCreators";
import {usePermissions} from "../../hooks/usePermissions";
import VerificationAlert from "../../components/Alerts/verification";


const Sites: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    // --- State for sorting ---
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc'); // Default to ascending (A-Z)

    const {status, isSelected, sitesData} = useSelector(selectSitesState);
    const {isOEM, isSuperUser, hasAccessToEdit,isAccountManager} = usePermissions();
    const sites = sitesData?.results;

    // --- Memoized sorting logic ---
    const sortedSites = useMemo(() => {
        // Return an empty array if sites is not yet available
        if (!sites) {
            return [];
        }

        // Create a copy to avoid mutating the Redux state
        return [...sites].sort((a, b) => {
            if (sortOrder === 'asc') {
                return a.title.localeCompare(b.title); // A-Z
            } else {
                return b.title.localeCompare(a.title); // Z-A
            }
        });
    }, [sites, sortOrder]); // Re-sort only when sites or sortOrder changes

    const isUpdatedSuccess = status === LoadingStatus.EDIT_SITE_SUCCESS;

    const onAddSite = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (hasAccessToEdit) {
            history.push("/sites/add/site");
        }
    };

    useEffect(() => {
        if (!isSelected) {
            dispatch(sitesAC.fetchSites());
        }
        dispatch(userAC.searchNotifications({
            offset: 0,
            is_active: true,
            ordering: `-date_created`,
        }));
    }, [dispatch, isSelected]);

    useEffect(() => {
        if (isUpdatedSuccess) {
            dispatch(sitesAC.fetchSites());
        }
    }, [dispatch, status, isUpdatedSuccess]);

    return (
        <Preloader isLoaded={status === LoadingStatus.LOADING}>
            <div className={classes.wrap}>
                <SitesAlert/>
                <UserAlert/>
                <HeaderSites/>
                <VerificationAlert/>
            {sortedSites && sortedSites.length > 0 && (
                    <div className={classes.controlsHeader}>
                        <Space align="center">
                            <span>Sort by Name</span>
                            <Tooltip title={`Sort ${sortOrder === 'asc' ? 'Descending (Z-A)' : 'Ascending (A-Z)'}`}>
                                <Button
                                    type="text"
                                    shape="circle"
                                    icon={sortOrder === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                    onClick={() => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                                />
                            </Tooltip>
                        </Space>
                    </div>
                )}
                <div className={classes.container}>



                    <div className={classes.mapBlock}>

                        {
                            sortedSites?.length === 0
                                ?
                                <Empty description="location is empty!"/>
                                :
                                sortedSites?.map((site, index) =>
                                    <SitesBlock {...site} key={`${site.title}${index}${site.id}`}/>)
                        }
                    </div>
                    {
                        (isOEM || isSuperUser || isAccountManager)
                        &&
                        <AddNewBlock text="Add New Site" onClick={onAddSite}/>
                    }

                </div>
            </div>
        </Preloader>
    );
};

export default Sites;
